import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { WordmarkLogo } from './Logo';
import { SHOW_SIGN_IN } from '../config/launch';
import { ArchImage, Label } from './System';
import Icon from './Icon';

// `hash` items only resolve on the home page, so from any other route we prefix "/" and
// let the browser land on the section after the SPA navigates home.
const NAV_GROUPS = [
  {
    label: 'Platform',
    image: 'facade',
    items: [
      {
        label: 'Conversational BI Chat',
        to: '/conversational-bi',
        desc: 'Ask in plain English and get the answer, the chart and the working, together.',
      },
      {
        label: 'Company Data Cloud',
        to: '/company-data-cloud',
        desc: 'Connect PostgreSQL or MySQL — or just upload a spreadsheet.',
      },
      {
        label: 'Pushdown SQL Engine',
        to: '/pushdown-sql-engine',
        desc: 'The engine behind the numbers: how a question becomes a calculation in your database.',
      },
    ],
  },
  {
    label: 'Resources',
    items: [
      {
        label: 'How It Works',
        hash: '#pipeline',
        desc: 'The three steps from a plain-English question to an answer you can check.',
      },
      {
        label: 'Documentation',
        to: '/documentation',
        desc: 'Connect a source, check the columns, ask a question, read the receipt.',
      },
      {
        label: 'Innovation & Architecture',
        to: '/innovation',
        desc: 'How Sidwell is built underneath — for the technically minded.',
      },
      {
        label: 'FAQ',
        to: '/faq',
        desc: 'What Sidwell connects to, what happens to your data, and where the product stands today.',
      },
      {
        label: 'Security & Privacy',
        to: '/security',
        desc: 'Where your data lives, who can see it, and how it is protected.',
      },
    ],
  },
];

const UTILITY = [
  { label: 'Security', to: '/security' },
  { label: 'Documentation', to: '/documentation' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Contact', href: 'mailto:hello@sidwell.ai' },
];

function NavItemLink({ item, hashHref, onClick, className, children }) {
  return item.to ? (
    <Link to={item.to} className={className} onClick={onClick}>
      {children}
    </Link>
  ) : (
    <a href={hashHref(item.hash)} className={className} onClick={onClick}>
      {children}
    </a>
  );
}

// Full-width mega panel aligned to the page grid.
function MegaPanel({ group, id, labelledBy, hashHref, onClose }) {
  return (
    <div
      id={id}
      role="region"
      aria-labelledby={labelledBy}
      className="absolute inset-x-0 top-full border-b border-wall/20 bg-white shadow-panel"
    >
      <div className="shell">
        <div className="grid-12 py-10">
          <div className="col-span-2">
            <Label>{group.label}</Label>
          </div>
          <ul className={`m-0 list-none border-b border-stone p-0 ${group.image ? 'col-span-7' : 'col-span-10'}`}>
            {group.items.map((item) => (
              <li key={item.label} className="border-t border-stone">
                <NavItemLink
                  item={item}
                  hashHref={hashHref}
                  onClick={onClose}
                  className="group/item grid min-h-[56px] grid-cols-12 items-baseline gap-4 py-3.5 transition-colors hover:text-bronze-deep"
                >
                  <span className="col-span-5 font-display text-[21px] leading-tight">{item.label}</span>
                  <span className="col-span-6 text-[15px] leading-snug text-glass">{item.desc}</span>
                  <span className="col-span-1 flex justify-end self-center">
                    <Icon name="arrow" size={16} className="opacity-0 transition-opacity group-hover/item:opacity-100 group-focus-visible/item:opacity-100" />
                  </span>
                </NavItemLink>
              </li>
            ))}
          </ul>
          {group.image && (
            <ArchImage name={group.image} settle={false} sizes="24vw" className="col-span-3 h-[210px]" />
          )}
        </div>
      </div>
    </div>
  );
}

function Header() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [openGroup, setOpenGroup] = React.useState(null);
  const [scrolled, setScrolled] = React.useState(false);
  const headerRef = React.useRef(null);
  const pinned = React.useRef(false);
  const closeTimer = React.useRef(null);
  const menuButton = React.useRef(null);
  const drawerClose = React.useRef(null);

  const closeMega = React.useCallback(() => {
    pinned.current = false;
    setOpenGroup(null);
  }, []);

  // Close every menu whenever the route (or hash) changes.
  React.useEffect(() => {
    setMenuOpen(false);
    closeMega();
  }, [location.pathname, location.hash, closeMega]);

  // The main tier condenses once the page has moved.
  React.useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setScrolled(window.scrollY > 48));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Prevent background scroll while the mobile drawer is open, and move focus in and out of it.
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    if (menuOpen) drawerClose.current?.focus();
    else if (wasOpen.current) menuButton.current?.focus();
    wasOpen.current = menuOpen;
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Escape closes whichever menu is open; a click outside the header dismisses the mega panel.
  React.useEffect(() => {
    if (!openGroup && !menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (menuOpen) setMenuOpen(false);
      if (openGroup) {
        const hadFocus = headerRef.current?.contains(document.activeElement);
        const button = document.getElementById(`nav-${openGroup}`);
        closeMega();
        if (hadFocus) button?.focus();
      }
    };
    const onDocClick = (e) => {
      if (!e.target.closest('[data-site-header]')) closeMega();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDocClick);
    };
  }, [openGroup, menuOpen, closeMega]);

  const hashHref = (hash) => (isHome ? hash : `/${hash}`);
  const closeDrawer = () => setMenuOpen(false);

  const hoverOpen = (label) => (e) => {
    if (e.pointerType !== 'mouse') return;
    clearTimeout(closeTimer.current);
    if (!pinned.current) setOpenGroup(label);
  };
  const hoverClose = (e) => {
    if (e.pointerType !== 'mouse' || pinned.current) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenGroup(null), 140);
  };
  const toggleGroup = (label) => {
    // A menu opened by hovering is pinned by the first click, and closed by the next.
    if (openGroup === label && pinned.current) {
      closeMega();
    } else {
      pinned.current = true;
      setOpenGroup(label);
    }
  };

  const activeGroup = NAV_GROUPS.find((g) => g.label === openGroup);
  const utilityLink = 'inline-flex min-h-[34px] items-center text-[13px] text-glass-light transition-colors hover:text-limestone';

  return (
    <>
      {/* Utility tier. It sits in the page flow, so it collapses away as the page scrolls. */}
      <div className="on-dark relative z-[51] hidden bg-wall lg:block">
        <nav aria-label="Utility" className="shell flex items-center justify-end gap-7">
          {UTILITY.map((u) =>
            u.to ? (
              <Link key={u.label} to={u.to} className={utilityLink}>
                {u.label}
              </Link>
            ) : (
              <a key={u.label} href={u.href} className={utilityLink}>
                {u.label}
              </a>
            )
          )}
          {SHOW_SIGN_IN && (
            <Link to="/signin" className={utilityLink}>
              Sign In
            </Link>
          )}
        </nav>
      </div>

      {/* Main tier. The wrapper keeps a constant height, so condensing never shifts the page. */}
      <header ref={headerRef} data-site-header className="sticky top-0 z-50 h-[72px]">
        <div
          className={`relative border-b bg-limestone transition-[height,border-color] duration-300 ${
            scrolled ? 'h-[60px] border-wall/20' : 'h-[72px] border-wall/10'
          }`}
          onPointerLeave={hoverClose}
        >
          <div className="shell flex h-full items-center justify-between">
            <Link to="/" aria-label="Sidwell — home" className="flex min-h-[44px] items-center">
              <WordmarkLogo className={`w-auto transition-[height] duration-300 ${scrolled ? 'h-[28px]' : 'h-[34px]'}`} />
            </Link>

            <nav aria-label="Main" className="hidden items-center gap-1 lg:flex" onBlur={(e) => {
              if (!headerRef.current?.contains(e.relatedTarget)) closeMega();
            }}>
              {NAV_GROUPS.map((group) => {
                const isOpen = openGroup === group.label;
                return (
                  <button
                    key={group.label}
                    id={`nav-${group.label}`}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`nav-${group.label}-panel`}
                    onClick={() => toggleGroup(group.label)}
                    onPointerEnter={hoverOpen(group.label)}
                    className={`flex min-h-[44px] items-center gap-1.5 px-4 text-[15px] font-medium transition-colors hover:text-bronze-deep ${
                      isOpen ? 'text-bronze-deep' : ''
                    }`}
                  >
                    {group.label}
                    <Icon name="chevron" size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                );
              })}
              <Link
                to="/about"
                onPointerEnter={() => {
                  if (!pinned.current) setOpenGroup(null);
                }}
                className="flex min-h-[44px] items-center px-4 text-[15px] font-medium transition-colors hover:text-bronze-deep"
              >
                About
              </Link>
              <a href={hashHref('#request-briefing')} className="btn-primary ml-5 !min-h-[44px] !py-2.5">
                Request Demo
              </a>
            </nav>

            <button
              ref={menuButton}
              type="button"
              className="-mr-2 inline-flex h-11 w-11 items-center justify-center lg:hidden"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMenuOpen(true)}
            >
              <Icon name="menu" size={24} />
            </button>
          </div>

          {activeGroup && (
            <div className="hidden lg:block">
              <MegaPanel
                group={activeGroup}
                id={`nav-${activeGroup.label}-panel`}
                labelledBy={`nav-${activeGroup.label}`}
                hashHref={hashHref}
                onClose={closeMega}
              />
            </div>
          )}
        </div>

        {/* Mobile: full-screen drawer with accordions */}
        <div
          id="mobile-menu"
          inert={!menuOpen}
          className={`fixed inset-0 z-[60] overflow-y-auto bg-limestone transition-opacity duration-200 lg:hidden ${
            menuOpen ? 'visible opacity-100' : 'invisible opacity-0'
          }`}
        >
          <div className="shell flex h-[72px] items-center justify-between border-b border-wall/15">
            <Link to="/" aria-label="Sidwell — home" onClick={closeDrawer} className="flex min-h-[44px] items-center">
              <WordmarkLogo className="h-[30px] w-auto" />
            </Link>
            <button
              ref={drawerClose}
              type="button"
              aria-label="Close menu"
              onClick={closeDrawer}
              className="-mr-2 inline-flex h-11 w-11 items-center justify-center"
            >
              <Icon name="close" size={24} />
            </button>
          </div>
          <div className="shell pb-10 pt-2">
            {NAV_GROUPS.map((group) => (
              <details key={group.label} className="group border-b border-wall/15">
                <summary className="flex min-h-[60px] cursor-pointer list-none items-center justify-between font-display text-[26px] [&::-webkit-details-marker]:hidden">
                  {group.label}
                  <Icon name="chevron" size={20} className="transition-transform group-open:rotate-180" />
                </summary>
                <ul className="m-0 list-none p-0 pb-4">
                  {group.items.map((item) => (
                    <li key={item.label} className="border-t border-stone">
                      <NavItemLink item={item} hashHref={hashHref} onClick={closeDrawer} className="block min-h-[44px] py-3">
                        <span className="block font-semibold">{item.label}</span>
                        <span className="mt-0.5 block text-[15px] leading-snug text-glass">{item.desc}</span>
                      </NavItemLink>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
            <Link to="/about" onClick={closeDrawer} className="flex min-h-[60px] items-center border-b border-wall/15 font-display text-[26px]">
              About
            </Link>
            <ul className="m-0 mt-6 grid list-none grid-cols-2 gap-x-6 p-0">
              {UTILITY.map((u) => (
                <li key={u.label}>
                  {u.to ? (
                    <Link to={u.to} onClick={closeDrawer} className="flex min-h-[44px] items-center text-glass">
                      {u.label}
                    </Link>
                  ) : (
                    <a href={u.href} className="flex min-h-[44px] items-center text-glass">
                      {u.label}
                    </a>
                  )}
                </li>
              ))}
              {SHOW_SIGN_IN && (
                <li>
                  <Link to="/signin" onClick={closeDrawer} className="flex min-h-[44px] items-center text-glass">
                    Sign In
                  </Link>
                </li>
              )}
            </ul>
            <a href={hashHref('#request-briefing')} onClick={closeDrawer} className="btn-primary mt-6 w-full">
              Request Demo
            </a>
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
